import React from 'react';
import PropTypes from 'prop-types';

import moment from 'moment-timezone';
import { SessionsDay } from './sessionsDay';
import { EmptyState, Icon, Card } from '@weezevent/nacre';
import Style from './sessionsCalendar.module.css';
import { capitalizeFirstLetter } from '../../../attendee/detail/components/utils';
import useAppContext from '../../../../context/useAppContext';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

const TitleSessionsMonth = React.memo(({ month, locale }) => {
    const _date = new Date(`${month}-01`);

    const _month = capitalizeFirstLetter(new Intl.DateTimeFormat(locale, { month: 'long' }).format(_date));
    const _year = new Intl.DateTimeFormat(locale, { year: 'numeric' }).format(_date);

    return (
        <div className={Style['sessions-month__title']}>
            <span className={Style['sessions-month__title-month']}>
                {_month}
                <span className={Style['sessions-month__title-year']}>{_year}</span>
            </span>
        </div>
    );
});

function SessionsMonth({ month, sessionsMonthTree, locale, selected, onSessionSelected, selectable, setSessionToDelete }) {
    return (
        <div className={`${Style['sessions-month']} ${selectable ? Style['sessions-month__selectable'] : ''}`}>
            <TitleSessionsMonth month={month} locale={locale} />
            {Object.entries(sessionsMonthTree).map(([day, sessions]) => {
                return (
                    <SessionsDay
                        key={`${month}-${day}`}
                        sessions={sessions}
                        locale={locale}
                        selected={selected}
                        onSessionSelected={onSessionSelected}
                        selectable={selectable}
                        setSessionToDelete={setSessionToDelete}
                    />
                );
            })}
        </div>
    );
}

export function SessionsCalendar({
    sessions = [],
    timezone = 'Europe/Paris',
    locale = 'en-GB',
    selected = [],
    onSessionSelected = () => {},
    selectable = false,
    onChangeDatesWithSessions = () => {},
    onChangeAnchorSelected = () => {},
    quotas = [],
    setSessionToDelete
}) {
    const { t } = useTranslation();
    const [{ event, organization }] = useAppContext();
    const history = useHistory();

    const sortSessions = React.useCallback(
        tree => {
            let datesWithSessions = {};

            //Sort keys (YYYY-MM) => (Asc) and sort sessions by time => (Asc)
            let _sessionsSorted = Object.keys(tree)
                .sort()
                .reduce((acc, yearMonth) => {
                    acc[yearMonth] = Object.entries(tree[yearMonth]).reduce((accu, [day, sessions]) => {
                        accu[day] = sessions.sort((s1, s2) => new Date(s1.start_date).getTime() - new Date(s2.start_date).getTime());
                        //Count sessions by day
                        datesWithSessions[`${yearMonth}-${day.padStart(2, '0')}`] = sessions.length;
                        return accu;
                    }, {});
                    return acc;
                }, {});

            onChangeDatesWithSessions(datesWithSessions);
            //after loading sessions, set first date available use by ListPreview for display first month with sessions available
            onChangeAnchorSelected(currentState => {
                if (!currentState) {
                    const keysDatesWithSessions = Object.keys(datesWithSessions);
                    const dateNow = moment().format('YYYY-MM-DD');
                    //First date with sessions
                    let firstDateWithSessions = keysDatesWithSessions.length ? keysDatesWithSessions[0] : null;
                    // if we find a date with sessions >= to now, we set in anchorSelected to scroll to it on page load
                    let indexFirstDateWithSessionsInFuture = keysDatesWithSessions.findIndex(date => date >= dateNow);
                    if (indexFirstDateWithSessionsInFuture !== -1) {
                        firstDateWithSessions = keysDatesWithSessions[indexFirstDateWithSessionsInFuture];
                    }

                    return firstDateWithSessions ? moment(firstDateWithSessions) : currentState;
                }
            });

            return _sessionsSorted;
        },
        [onChangeDatesWithSessions, onChangeAnchorSelected]
    );

    const sessionsTree = React.useMemo(() => {
        //Group by YYYY-MM and group by DD
        let tmp = sessions.reduce((acc, obj) => {
            let _obj = obj.copy();
            //Localize start_date
            let dateLocalizedStart = moment.utc(_obj.start_date).tz(timezone);
            _obj.start_date = dateLocalizedStart.format('YYYY-MM-DD HH:mm');

            //Check if session is unavailable
            let now = moment
                .utc()
                .tz(timezone)
                .format('YYYY-MM-DD HH:mm');
            _obj.unavailable = _obj.start_date < now;
            const seanceQuota = quotas.find(q => q.session_id === obj.weez_date_id);
            _obj.quota_total = seanceQuota ? seanceQuota.quota_total : null;
            _obj.quota_taken = seanceQuota ? seanceQuota.quota_taken : 0;

            //Construct tree sessions
            let keyYearMonth = dateLocalizedStart.format('YYYY-MM');
            let keyDay = dateLocalizedStart.format('D');
            acc[keyYearMonth] = acc[keyYearMonth] || {};
            acc[keyYearMonth][keyDay] = acc[keyYearMonth][keyDay] || [];
            acc[keyYearMonth][keyDay].push(_obj);

            return acc;
        }, {});

        return sortSessions(tmp);
    }, [quotas, sessions, timezone, onChangeDatesWithSessions]);

    if (sessions.length) {
        return Object.keys(sessionsTree).map(month => {
            return (
                <SessionsMonth
                    key={month}
                    month={month}
                    sessionsMonthTree={sessionsTree[month]}
                    locale={locale}
                    selected={selected}
                    onSessionSelected={onSessionSelected}
                    selectable={selectable}
                    setSessionToDelete={setSessionToDelete}
                />
            );
        });
    }

    return (
        <Card className={Style['empty-state__container']}>
            <EmptyState
                icon={<Icon name="list" size="regular" />}
                title={t('ticket.sessions.list.emptyState.title')}
                labelButton={t('ticket.sessions.list.emptyState.labelButton')}
                onClick={() => {
                    history.push(`/ticket/O${organization.id}/E${event.id}/settings/sessions/new`);
                }}
            />
        </Card>
    );
}

SessionsCalendar.propTypes = {
    sessions: PropTypes.array,
    timezone: PropTypes.string,
    locale: PropTypes.string,
    selected: PropTypes.array,
    onSessionSelected: PropTypes.func,
    selectable: PropTypes.bool,
    onChangeDatesWithSessions: PropTypes.func
};
